import { useRef, useState, type FormEvent, type RefObject } from 'react';
import type { Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useCreateProject } from '@/api/hooks';
import { focusElement, useCreateFormState } from '@/lib/useFormState';
import { INITIAL_NEW_PROJECT, applyNewProjectChange, buildNewProjectBody, createdDescription, invalidFocusTarget, newProjectErrors, validateNewProject, type NewProjectField } from './projectFormModel';

type SlugRef = RefObject<HTMLInputElement | null> | undefined;
type CreateFormState = ReturnType<typeof useCreateFormState>;

/** The values of the New project form and the messages under each field. */
function useNewProjectFields(form: CreateFormState) {
  const [values, setValues] = useState(INITIAL_NEW_PROJECT);
  const local = validateNewProject(values);
  return {
    values,
    local,
    errors: newProjectErrors(local, form.server.fields, form.submitted, values),
    change: (field: NewProjectField, raw: string) => {
      setValues((v) => applyNewProjectChange(v, field, raw));
      // The API's message under the description stays until the form is submitted again.
      if (field !== 'description') form.clearField(field);
    },
    setDefaults: (defaults: boolean) => setValues((v) => ({ ...v, defaults })),
  };
}

interface SubmitDeps {
  onDone: (project: Project) => void;
  slugRef: SlugRef;
  nameRef: RefObject<HTMLInputElement | null>;
  create: ReturnType<typeof useCreateProject>;
  form: CreateFormState;
  fields: ReturnType<typeof useNewProjectFields>;
}

/** Validates, creates the project and hands it over; a failure puts the API's messages under their fields. */
function useNewProjectSubmit({ onDone, slugRef, nameRef, create, form, fields }: SubmitDeps) {
  const focusInvalid = (target: 'productionBranch' | 'slug' | 'name') => {
    if (target === 'productionBranch') focusElement('project-production-branch');
    else (target === 'slug' ? slugRef : nameRef)?.current?.focus();
  };
  return async (e: FormEvent) => {
    e.preventDefault();
    form.begin();
    const invalid = invalidFocusTarget(fields.local);
    if (invalid) return focusInvalid(invalid);
    try {
      const project = await create.mutateAsync({ body: buildNewProjectBody(fields.values), idempotencyKey: form.idempotencyKey() });
      form.renewKey();
      toast.success(`Project ${project.name} created`, { description: createdDescription(fields.values) });
      onDone(project);
    } catch (err) {
      if (form.fail(err).slug) slugRef?.current?.focus();
    }
  };
}

/** Everything the New project form needs: its values, messages, the name input ref and the submit handler. */
export function useNewProjectForm({ onDone, slugRef }: { onDone: (project: Project) => void; slugRef: SlugRef }) {
  const create = useCreateProject();
  const form = useCreateFormState();
  const nameRef = useRef<HTMLInputElement>(null);
  const fields = useNewProjectFields(form);
  const onSubmit = useNewProjectSubmit({ onDone, slugRef, nameRef, create, form, fields });
  return { ...fields, nameRef, pending: create.isPending, formError: form.server.form, onSubmit };
}
